import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtCurrency, stageMeta, STAGES, DEFAULT_SYMBOL } from "@/lib/api";
import { useCurrencies, currencySymbol } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { Search, Plus, Edit2, Pencil, Trash2, FileText, Mail, Globe2, Phone, MapPin, Tag, X, MessageCircle, Upload, User, Briefcase, UserCheck } from "lucide-react";
import Modal from "@/components/Modal";
import CustomerForm from "@/components/CustomerForm";
import QuotationBuilder from "@/components/QuotationBuilder";
import SendMessageModal from "@/components/SendMessageModal";
import BulkEmailModal from "@/components/BulkEmailModal";
import ImportCustomersModal from "@/components/ImportCustomersModal";
import { toast } from "sonner";

const firstEmail = (c) => (c?.emails && c.emails[0]) || "";
const firstPhone = (c) => (c?.phones && c.phones[0]) || "";

const colorForName = (name) => {
  const colors = ["#C85A17", "#A6470F", "#1E3A5F", "#2F4F4F", "#6B6965", "#7C2D12"];
  const c = name?.charCodeAt(0) || 0;
  return colors[c % colors.length];
};

function Avatar({ name, size = 36 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: 6, background: colorForName(name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontFamily: "Cabinet Grotesk", fontSize: size * 0.4, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function StagePill({ stage }) {
  const m = stageMeta(stage);
  return (
    <span className="tag" style={{ background: m.bg, borderColor: "var(--border)", color: m.accent }} data-testid={`stage-pill-${stage}`}>
      {m.label}
    </span>
  );
}

const emptyDealForm = () => ({ title: "", requirements: "", stage: "lead", tags: "" });

const PAGE_SIZE = 25;

function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("…");
    result.push(sorted[i]);
  }
  return result;
}

export default function Customers() {
  const { isAdmin } = useAuth();
  const currencies = useCurrencies();
  const [customers, setCustomers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [assignTarget, setAssignTarget] = useState("");
  const [pageDeals, setPageDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterCountryDebounced, setFilterCountryDebounced] = useState("");
  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'quote' | 'edit-quote' | 'email' | 'whatsapp' | 'import' | 'deal'
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [activeQuoteId, setActiveQuoteId] = useState(null);
  const [activeEditQuote, setActiveEditQuote] = useState(null);
  const [activeDeal, setActiveDeal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [customerDeals, setCustomerDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [note, setNote] = useState("");
  const [dealForm, setDealForm] = useState(emptyDealForm());
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [pageJump, setPageJump] = useState("");
  const skipPageScroll = useRef(true);

  const dealsFor = (id) => pageDeals.filter((d) => d.customer_id === id);

  const reloadDetailActivity = async (id) => {
    const r = await api.get("/activities", { params: { customer_id: id } });
    setActivities(r.data);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilterCountryDebounced(filterCountry);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterCountry]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        search: searchDebounced.trim(),
      };
      if (filterSource !== "all") params.source = filterSource;
      if (filterCountryDebounced.trim()) params.country = filterCountryDebounced.trim();
      const { data } = await api.get("/customers", { params });
      setCustomers(data.items || []);
      setTotal(data.total ?? 0);
      setSources(data.sources || []);
      const ids = (data.items || []).map((c) => c.id);
      if (ids.length) {
        const dRes = await api.get("/deals", { params: { customer_ids: ids.join(",") } });
        setPageDeals(dRes.data);
      } else {
        setPageDeals([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load customers");
    } finally {
      setLoading(false);
    }
  }, [page, searchDebounced, filterSource, filterCountryDebounced]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setSalesUsers(r.data)).catch(() => {});
  }, [isAdmin]);

  const reloadDetail = async (id) => {
    const [a, q, d] = await Promise.all([
      api.get("/activities", { params: { customer_id: id } }),
      api.get("/quotations", { params: { customer_id: id } }),
      api.get("/deals", { params: { customer_id: id } }),
    ]);
    setActivities(a.data);
    setQuotes(q.data);
    setCustomerDeals(d.data);
  };

  useEffect(() => {
    if (!detail) { setActivities([]); setQuotes([]); setCustomerDeals([]); return; }
    reloadDetail(detail.id);
  }, [detail]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (!loading && customers.length === 0 && page > 1 && total > 0) {
      setPage(page - 1);
    }
  }, [loading, customers.length, page, total]);

  useEffect(() => {
    if (skipPageScroll.current) {
      skipPageScroll.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [safePage]);

  const goToPage = (raw) => {
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n) || n < 1) return;
    setPage(Math.min(pageCount, Math.max(1, n)));
    setPageJump("");
  };

  const pageIds = customers.map((c) => c.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleSelectPage = () => setSelected((s) =>
    allPageSelected ? s.filter((id) => !pageIds.includes(id)) : [...new Set([...s, ...pageIds])]
  );

  const onBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Remove ${selected.length} customer${selected.length > 1 ? "s" : ""}? This also deletes their leads, quotations & activity.`)) return;
    try {
      await api.post("/customers/bulk-delete", { ids: selected });
      toast.success(`Removed ${selected.length} customer${selected.length > 1 ? "s" : ""}`);
      if (detail && selected.includes(detail.id)) setDetail(null);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete customers");
    }
  };

  const onBulkAssign = async () => {
    if (selected.length === 0) return;
    const target = salesUsers.find((u) => u.id === assignTarget);
    const label = target ? target.name : "Unassigned";
    if (!window.confirm(`Assign ${selected.length} customer${selected.length > 1 ? "s" : ""} to ${label}?`)) return;
    try {
      await api.post("/customers/bulk-assign", { ids: selected, sales_person_id: assignTarget });
      toast.success(`Assigned ${selected.length} customer${selected.length > 1 ? "s" : ""} to ${label}`);
      if (detail && selected.includes(detail.id)) {
        const { data } = await api.get(`/customers/${detail.id}`);
        setDetail(data);
      }
      setSelected([]);
      setAssignTarget("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign customers");
    }
  };

  const onAdd = async (form) => {
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add customer");
    }
  };

  const onEdit = async (form) => {
    try {
      await api.patch(`/customers/${activeCustomer.id}`, form);
      toast.success("Updated");
      setModal(null);
      setActiveCustomer(null);
      if (detail?.id === activeCustomer.id) {
        const { data } = await api.get(`/customers/${activeCustomer.id}`);
        setDetail(data);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update customer");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this customer? This will also delete its leads, quotations & activity.")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Removed");
    if (detail?.id === id) setDetail(null);
    setSelected((s) => s.filter((x) => x !== id));
    load();
  };

  const onCreateDeal = async () => {
    if (!detail) return;
    const payload = {
      customer_id: detail.id,
      title: dealForm.title.trim(),
      requirements: dealForm.requirements.trim(),
      stage: dealForm.stage,
      tags: dealForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      sales_person_id: detail.sales_person_id || "",
      sales_person_name: detail.sales_person_name || "",
    };
    await api.post("/deals", payload);
    toast.success("Lead added");
    setModal(null);
    setDealForm(emptyDealForm());
    await reloadDetail(detail.id);
    load();
  };

  const addNote = async () => {
    if (!note.trim() || !detail) return;
    await api.post("/activities", { customer_id: detail.id, type: "note", message: note.trim() });
    setNote("");
    reloadDetailActivity(detail.id);
    toast.success("Note added");
  };

  return (
    <div data-testid="customers-page">
      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 mb-4" style={{ padding: "10px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 }} data-testid="bulk-actions">
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.length} selected</span>
          <button onClick={() => setSelected([])} className="btn-ghost" style={{ fontSize: 12 }}>Clear</button>
          <button onClick={() => setModal("bulk-email")} className="btn-outline" data-testid="bulk-email-btn">
            <Mail size={14} /> Send mail
          </button>
          {isAdmin && (
            <div className="flex items-center gap-2 ml-auto">
              <select
                value={assignTarget}
                onChange={(e) => setAssignTarget(e.target.value)}
                className="input-base"
                style={{ padding: "7px 10px", fontSize: 13, width: "auto" }}
                data-testid="bulk-assign-select"
              >
                <option value="">— Unassigned —</option>
                {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button onClick={onBulkAssign} className="btn-outline" data-testid="bulk-assign-btn">
                <UserCheck size={14} /> Assign selected
              </button>
            </div>
          )}
          <button onClick={onBulkDelete} className="btn-outline" style={{ color: "var(--danger)", borderColor: "var(--danger)", marginLeft: isAdmin ? 0 : "auto" }} data-testid="bulk-delete-btn">
            <Trash2 size={14} /> Delete selected
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers…" className="input-base" style={{ paddingLeft: 36 }} data-testid="customers-search" />
        </div>
        <div className="flex gap-2 items-center" data-testid="country-filter">
          <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Country:</span>
          <input
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            placeholder="Search country…"
            className="input-base"
            style={{ padding: "7px 10px", fontSize: 13, width: 160 }}
            data-testid="filter-country"
          />
        </div>
        {sources.length > 0 && (
          <div className="flex gap-2 items-center" data-testid="source-filter">
            <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Sourced from:</span>
            <select value={filterSource} onChange={(e) => { setFilterSource(e.target.value); setPage(1); }} className="input-base" style={{ padding: "7px 10px", fontSize: 13, width: "auto" }} data-testid="filter-source">
              <option value="all">All</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setModal("import")} className="btn-outline" data-testid="import-customers-btn">
            <Upload size={15} /> Import CSV
          </button>
          <button onClick={() => setModal("add")} className="btn-primary" data-testid="add-customer-btn">
            <Plus size={15} /> Add Customer
          </button>
        </div>
      </div>

      {/* Count */}
      {!loading && (
        <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }} data-testid="customers-count">
          <strong style={{ color: "var(--text)", fontWeight: 700 }}>{total}</strong> {total === 1 ? "customer" : "customers"}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="loading-state">
          <div style={{ fontSize: 14 }}>Loading customers…</div>
        </div>
      ) : customers.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="empty-state">
          <div style={{ fontSize: 14 }}>No customers match your filters.</div>
        </div>
      ) : (
        <div className="card-ledger overflow-hidden" data-testid="customers-list">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--surface-2)" }}>
                <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)", width: 40 }}>
                  <input type="checkbox" checked={allPageSelected} onChange={toggleSelectPage} title="Select all on this page" data-testid="select-all-checkbox" style={{ cursor: "pointer" }} />
                </th>
                {["Company", "Contact", "Country / City", "Requirement", "Quality", "Sales Person", "Lead Stages", ""].map((h, i) => (
                  <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.map((c, i) => {
                const ds = dealsFor(c.id);
                return (
                <tr
                  key={c.id}
                  onClick={() => setDetail(c)}
                  className="stagger"
                  style={{ borderBottom: "1px solid var(--border)", cursor: "pointer", animationDelay: `${i * 30}ms` }}
                  data-testid={`customer-row-${c.id}`}
                >
                  <td style={{ padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} data-testid={`select-checkbox-${c.id}`} style={{ cursor: "pointer" }} />
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <div className="flex items-center gap-3">
                      <Avatar name={c.company} size={36} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{c.company}</div>
                        <div style={{ fontSize: 12, color: "var(--text-2)" }}>{ds[0]?.title?.slice(0, 60) || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13 }}>
                    <div>{c.contact || "—"}</div>
                    {firstEmail(c) && <div style={{ fontSize: 11, color: "var(--text-2)" }}>{firstEmail(c)}</div>}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>
                    <div>{c.country || "—"}</div>
                    {c.city_or_state && <div style={{ fontSize: 11 }}>{c.city_or_state}</div>}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)", maxWidth: 160 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.requirement || "—"}</div>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{c.quality || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{c.sales_person_name || "—"}</td>
                  <td style={{ padding: "14px 16px" }} data-testid={`customer-deals-${c.id}`}>
                    {ds.length === 0 ? (
                      <span style={{ color: "var(--text-2)", fontSize: 13 }}>—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {ds.map((d) => <StagePill key={d.id} stage={d.stage} />)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setActiveCustomer(c); setActiveQuoteId(null); setModal("email"); }} className="btn-ghost" title="Send Email" data-testid={`email-btn-${c.id}`} disabled={!firstEmail(c)}>
                        <Mail size={14} />
                      </button>
                      <button onClick={() => { setActiveCustomer(c); setActiveQuoteId(null); setModal("whatsapp"); }} className="btn-ghost" title="Send WhatsApp" data-testid={`whatsapp-btn-${c.id}`} disabled={!firstPhone(c)} style={{ color: firstPhone(c) ? "#25D366" : undefined }}>
                        <MessageCircle size={14} />
                      </button>
                      <button onClick={() => { setActiveCustomer(c); setModal("edit"); }} className="btn-ghost" title="Edit" data-testid={`edit-btn-${c.id}`}>
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => onDelete(c.id)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Delete" data-testid={`delete-btn-${c.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="pagination">
          <div style={{ fontSize: 13, color: "var(--text-2)" }}>
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} className="btn-outline" disabled={safePage <= 1} data-testid="page-prev">Prev</button>
            {pageNumbers(safePage, pageCount).map((p, i) => (
              typeof p === "number" ? (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={p === safePage ? "btn-primary" : "btn-outline"}
                  style={{ minWidth: 36, padding: "6px 10px", fontSize: 13 }}
                  data-testid={`page-num-${p}`}
                >
                  {p}
                </button>
              ) : (
                <span key={`ellipsis-${i}`} style={{ fontSize: 13, color: "var(--text-2)", padding: "0 4px" }}>{p}</span>
              )
            ))}
            <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} className="btn-outline" disabled={safePage >= pageCount} data-testid="page-next">Next</button>
            <form
              onSubmit={(e) => { e.preventDefault(); goToPage(pageJump); }}
              className="flex items-center gap-2"
              style={{ marginLeft: 4 }}
              data-testid="page-jump-form"
            >
              <span style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>Go to</span>
              <input
                type="number"
                min={1}
                max={pageCount}
                value={pageJump}
                onChange={(e) => setPageJump(e.target.value)}
                placeholder={String(safePage)}
                className="input-base"
                style={{ width: 64, padding: "6px 8px", fontSize: 13 }}
                data-testid="page-jump-input"
              />
              <button type="submit" className="btn-outline" style={{ fontSize: 13 }} data-testid="page-jump-btn">Go</button>
            </form>
          </div>
        </div>
      )}

      {/* Detail slide-over */}
      {detail && (
        <>
          <div onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(28,27,26,0.4)", zIndex: 150 }} className="fade-in" />
          <aside className="slide-in" style={{ position: "fixed", top: 0, right: 0, width: "min(560px, 100%)", height: "100vh", background: "var(--surface)", zIndex: 160, borderLeft: "1px solid var(--border)", display: "flex", flexDirection: "column" }} data-testid="customer-detail-panel">
            <div className="flex items-start justify-between px-6 py-5 divider-h">
              <div className="flex gap-4 items-center min-w-0">
                <Avatar name={detail.company} size={48} />
                <div style={{ minWidth: 0 }}>
                  <div className="font-display" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{detail.company}</div>
                  <div style={{ fontSize: 13, color: "var(--text-2)" }}>{detail.contact}</div>
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="btn-ghost" data-testid="detail-close"><X size={18} /></button>
            </div>

            <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
              <div className="overline" style={{ fontSize: 10, marginBottom: 8 }}>Contact</div>
              <div className="grid gap-2 mb-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {[
                  ...(detail.emails || []).map((e) => [Mail, e, `mailto:${e}`]),
                  ...(detail.phones || []).map((p) => [Phone, p, `tel:${p}`]),
                  [Globe2, detail.website, detail.website ? `https://${detail.website.replace(/^https?:\/\//, "")}` : null],
                  [MapPin, detail.country, null],
                  [User, detail.sales_person_name, null],
                ].map(([Icon, val, href], i) => val ? (
                  <a key={i} href={href || undefined} target={href ? "_blank" : undefined} rel="noreferrer" className="flex items-center gap-2" style={{ fontSize: 13, color: href ? "var(--primary)" : "var(--text)", textDecoration: "none" }}>
                    <Icon size={14} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val}</span>
                  </a>
                ) : null)}
              </div>

              {/* Profile */}
              {(detail.rating > 0 || detail.address || detail.sourced_from || detail.customer_since || detail.city_or_state || detail.requirement || detail.quality) && (
                <>
                  <div className="overline" style={{ fontSize: 10, marginBottom: 8 }}>Profile</div>
                  <div className="grid gap-2 mb-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
                    {detail.city_or_state && (
                      <div style={{ fontSize: 13 }}><span style={{ color: "var(--text-2)" }}>City / State:</span> {detail.city_or_state}</div>
                    )}
                    {detail.rating > 0 && (
                      <div className="flex items-center gap-2" style={{ fontSize: 13 }}>
                        <span style={{ color: "var(--text-2)" }}>Rating:</span>
                        <span style={{ color: "var(--primary)", letterSpacing: 2 }}>{"★".repeat(detail.rating)}<span style={{ color: "var(--border)" }}>{"★".repeat(5 - detail.rating)}</span></span>
                      </div>
                    )}
                    {detail.sourced_from && (
                      <div style={{ fontSize: 13 }}><span style={{ color: "var(--text-2)" }}>Sourced from:</span> {detail.sourced_from}</div>
                    )}
                    {detail.customer_since && (
                      <div style={{ fontSize: 13 }}><span style={{ color: "var(--text-2)" }}>Customer since:</span> {detail.customer_since}</div>
                    )}
                    {detail.address && (
                      <div style={{ fontSize: 13, gridColumn: "1 / -1" }}><span style={{ color: "var(--text-2)" }}>Address:</span> {detail.address}</div>
                    )}
                    {detail.requirement && (
                      <div style={{ fontSize: 13, gridColumn: "1 / -1" }}><span style={{ color: "var(--text-2)" }}>Requirement:</span> {detail.requirement}</div>
                    )}
                    {detail.quality && (
                      <div style={{ fontSize: 13, gridColumn: "1 / -1" }}><span style={{ color: "var(--text-2)" }}>Quality:</span> {detail.quality}</div>
                    )}
                  </div>
                </>
              )}

              {/* Deals */}
              <div className="flex items-center justify-between mt-4 mb-3">
                <div className="overline" style={{ fontSize: 10 }}>Leads ({customerDeals.length})</div>
                <button onClick={() => { setDealForm(emptyDealForm()); setModal("deal"); }} className="btn-ghost" style={{ color: "var(--primary)" }} data-testid="detail-new-deal">
                  <Plus size={13} /> New lead
                </button>
              </div>
              {customerDeals.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-2)", padding: "8px 0" }}>No leads yet.</div>
              ) : (
                <div className="card-ledger" style={{ overflow: "hidden", marginBottom: 8 }}>
                  {customerDeals.map((d, i) => (
                    <div key={d.id} style={{ padding: "12px 14px", borderTop: i === 0 ? "none" : "1px solid var(--border)" }} data-testid={`detail-deal-${d.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{d.title || "Untitled lead"}</div>
                          <div className="mt-1"><StagePill stage={d.stage} /></div>
                        </div>
                        <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                          <div className="font-mono-data" style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--primary)" }}>FOB {fmtCurrency(d.fob_value, d.currency, currencySymbol(d.currency, currencies))}</div>
                            <div style={{ fontSize: 11, color: "var(--text-2)" }}>CIF {fmtCurrency(d.cif_value, d.currency, currencySymbol(d.currency, currencies))}</div>
                            <div style={{ fontSize: 11, color: "var(--text-2)" }}>Ex-Works {fmtCurrency(d.exworks_value, d.currency, currencySymbol(d.currency, currencies))}</div>
                          </div>
                          <button onClick={() => { setActiveCustomer(detail); setActiveDeal(d); setModal("quote"); }} className="btn-ghost" style={{ padding: 5 }} title="Make quotation for this lead" data-testid={`deal-quote-${d.id}`}>
                            <FileText size={13} />
                          </button>
                        </div>
                      </div>
                      {d.requirements && <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 6, lineHeight: 1.4 }}>{d.requirements}</div>}
                      {(d.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {d.tags.map((t) => (
                            <span key={t} className="tag" style={{ fontSize: 9, padding: "1px 6px", background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text-2)" }}>
                              <Tag size={9} /> {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Quotations */}
              <div className="flex items-center justify-between mt-6 mb-3">
                <div className="overline" style={{ fontSize: 10 }}>Quotations ({quotes.length})</div>
              </div>
              {quotes.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-2)", padding: "8px 0" }}>No quotations yet — use the <FileText size={11} style={{ display: "inline", verticalAlign: "middle" }} /> button on a lead above to create one.</div>
              ) : (
                <div className="card-ledger" style={{ overflow: "hidden" }}>
                  {quotes.map((q, i) => (
                    <div key={q.id} className="flex items-center justify-between" style={{ padding: "10px 14px", borderTop: i === 0 ? "none" : "1px solid var(--border)" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }} className="font-mono-data">{q.quote_no}</div>
                        <div style={{ fontSize: 11, color: "var(--text-2)" }}>{new Date(q.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="font-mono-data" style={{ fontSize: 13, fontWeight: 600, color: "var(--primary)", textAlign: "right" }} title="FOB / CIF / Ex-Works">
                          FOB {q.currency_symbol || DEFAULT_SYMBOL}{Number(q.fob_total || 0).toLocaleString("en-US")}
                        </div>
                        <button onClick={() => { setActiveEditQuote(q); setActiveCustomer(detail); setModal("edit-quote"); }} className="btn-ghost" style={{ padding: 5 }} title="Edit this quote" data-testid={`quote-edit-${q.id}`}>
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => { setActiveCustomer(detail); setActiveQuoteId(q.id); setModal("email"); }} className="btn-ghost" style={{ padding: 5 }} title="Email this quote" data-testid={`quote-email-${q.id}`} disabled={!firstEmail(detail)}>
                          <Mail size={13} />
                        </button>
                        <button onClick={() => { setActiveCustomer(detail); setActiveQuoteId(q.id); setModal("whatsapp"); }} className="btn-ghost" style={{ padding: 5, color: firstPhone(detail) ? "#25D366" : undefined }} title="WhatsApp this quote" data-testid={`quote-wa-${q.id}`} disabled={!firstPhone(detail)}>
                          <MessageCircle size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Notes & activity */}
              <div className="overline mt-6 mb-3" style={{ fontSize: 10 }}>Activity & Notes</div>
              <div className="flex gap-2 mb-3">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a quick note…" className="input-base" data-testid="note-input" onKeyDown={(e) => e.key === "Enter" && addNote()} />
                <button onClick={addNote} className="btn-outline" data-testid="note-add">Add</button>
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {activities.map((a) => (
                  <li key={a.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 6, height: 6, borderRadius: 6, background: "var(--primary)", marginTop: 8, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>{a.message}</div>
                      <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>{a.type} · {new Date(a.created_at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-2 justify-between px-6 py-4" style={{ borderTop: "1px solid var(--border)" }}>
              <button onClick={() => { setActiveCustomer(detail); setModal("edit"); }} className="btn-outline" data-testid="detail-edit">
                <Edit2 size={14} /> Edit
              </button>
              <div className="flex gap-2">
                <button onClick={() => { setDealForm(emptyDealForm()); setModal("deal"); }} className="btn-outline" data-testid="detail-deal">
                  <Briefcase size={14} /> Lead
                </button>
                <button onClick={() => { setActiveCustomer(detail); setActiveQuoteId(null); setModal("email"); }} className="btn-outline" disabled={!firstEmail(detail)} title={firstEmail(detail) ? "Send email" : "Add an email first"} data-testid="detail-email">
                  <Mail size={14} /> Email
                </button>
              </div>
            </div>
          </aside>
        </>
      )}

      {modal === "add" && (
        <Modal title="Add Customer" subtitle="Capture lead or account details" onClose={() => setModal(null)} size="lg">
          <CustomerForm onSave={onAdd} onCancel={() => setModal(null)} />
        </Modal>
      )}

      {modal === "edit" && activeCustomer && (
        <Modal title={`Edit · ${activeCustomer.company}`} onClose={() => { setModal(null); setActiveCustomer(null); }} size="lg">
          <CustomerForm initial={activeCustomer} onSave={onEdit} onCancel={() => { setModal(null); setActiveCustomer(null); }} submitLabel="Save Changes" />
        </Modal>
      )}

      {modal === "deal" && detail && (
        <Modal title={`New Lead · ${detail.company}`} subtitle="A sales opportunity in the pipeline" onClose={() => setModal(null)} size="md">
          <div className="flex flex-col gap-4" data-testid="deal-form">
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Title</label>
              <input value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} placeholder="e.g. A4 Copier Paper — Q3 reorder" className="input-base" data-testid="deal-title" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Requirements</label>
              <textarea value={dealForm.requirements} onChange={(e) => setDealForm({ ...dealForm, requirements: e.target.value })} placeholder="What the customer needs…" className="input-base" rows={3} data-testid="deal-requirements" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Stage</label>
              <select value={dealForm.stage} onChange={(e) => setDealForm({ ...dealForm, stage: e.target.value })} className="input-base" data-testid="deal-stage">
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10 }}>Tags (comma-separated)</label>
              <input value={dealForm.tags} onChange={(e) => setDealForm({ ...dealForm, tags: e.target.value })} placeholder="export, recurring" className="input-base" data-testid="deal-tags" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setModal(null)} className="btn-outline">Cancel</button>
              <button onClick={onCreateDeal} className="btn-primary" data-testid="deal-save">Create Lead</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "quote" && activeCustomer && (
        <Modal title={`Quotation · ${activeCustomer.company}`} subtitle="quote" onClose={() => { setModal(null); setActiveCustomer(null); setActiveDeal(null); }} size="xl">
          <QuotationBuilder
            customer={activeCustomer}
            deal={activeDeal}
            onClose={() => { setModal(null); setActiveCustomer(null); setActiveDeal(null); }}
            onSaved={() => {
              setModal(null);
              setActiveCustomer(null);
              setActiveDeal(null);
              if (detail) reloadDetail(detail.id);
              load();
            }}
          />
        </Modal>
      )}

      {modal === "edit-quote" && activeCustomer && activeEditQuote && (
        <Modal title={`Edit Quotation · ${activeEditQuote.quote_no}`} subtitle={activeCustomer.company} onClose={() => { setModal(null); setActiveCustomer(null); setActiveEditQuote(null); }} size="xl">
          <QuotationBuilder
            customer={activeCustomer}
            existingQuote={activeEditQuote}
            onClose={() => { setModal(null); setActiveCustomer(null); setActiveEditQuote(null); }}
            onSaved={() => {
              setModal(null);
              setActiveCustomer(null);
              setActiveEditQuote(null);
              if (detail) reloadDetail(detail.id);
              load();
            }}
          />
        </Modal>
      )}

      {modal === "import" && (
        <ImportCustomersModal
          onClose={() => setModal(null)}
          onImported={(opts) => { load(); if (!opts?.keepOpen) setModal(null); }}
        />
      )}

      {modal === "bulk-email" && selected.length > 0 && (
        <BulkEmailModal
          customerIds={selected}
          onClose={() => setModal(null)}
        />
      )}

      {(modal === "email" || modal === "whatsapp") && activeCustomer && (
        <SendMessageModal
          channel={modal}
          customer={activeCustomer}
          quoteId={activeQuoteId}
          onClose={() => { setModal(null); setActiveCustomer(null); setActiveQuoteId(null); }}
          onSent={() => { if (detail) reloadDetailActivity(detail.id); }}
        />
      )}
    </div>
  );
}
